import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, ShieldCheck, ShieldAlert, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * TwoFactorSetup — drop-in panel for any settings page.
 * Handles three sub-states: not enrolled, enrolling (QR shown), enrolled.
 */
export default function TwoFactorSetup({ onChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("idle"); // idle | setup | confirming | enabled
  const [setupPayload, setSetupPayload] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copied, setCopied] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/security/status");
      setStatus(r.data);
    } catch (err) {
      toast.error("Could not load security status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetupPayload(r.data);
      setStage("setup");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start 2FA setup");
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setVerifying(true);
    try {
      const r = await api.post("/auth/2fa/verify-setup", { code: code.trim() });
      setRecoveryCodes(r.data.recovery_codes || []);
      setStage("enabled");
      setCode("");
      await load();
      onChange?.();
      toast.success("Two-factor authentication enabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  const copyRecovery = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const disable = async (e) => {
    e.preventDefault();
    setDisabling(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode.trim() });
      setDisableCode("");
      setStage("idle");
      await load();
      onChange?.();
      toast.success("Two-factor authentication disabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not disable 2FA");
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--pa-text-muted)]" data-testid="tfa-loading">
        <Loader2 size={14} className="animate-spin" /> Loading security status…
      </div>
    );
  }

  const enrolled = !!status?.totp_enabled;
  const grace = status?.grace_days_remaining;
  const needsNow = status?.needs_enrollment;

  return (
    <div className="bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-5" data-testid="tfa-panel">
      <div className="flex items-center gap-2 mb-1">
        {enrolled ? (
          <ShieldCheck size={16} className="text-emerald-400" />
        ) : (
          <ShieldAlert size={16} className={needsNow ? "text-red-400" : "text-amber-400"} />
        )}
        <h3 className="font-serif text-lg text-[var(--pa-text)]">Two-factor authentication</h3>
        {enrolled && <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-400" data-testid="tfa-status-on">Active</span>}
      </div>
      <p className="text-xs text-[var(--pa-text-muted)] mb-4">
        {enrolled
          ? "An authenticator code is required at every sign-in."
          : needsNow
          ? "Your 7-day grace period has expired. You must enroll in 2FA now to keep using ProsthoAI."
          : grace != null
          ? `Optional for ${grace} more day${grace === 1 ? "" : "s"}, then required. Enroll now to avoid being locked out.`
          : "Add a one-time code from your phone's authenticator app to every sign-in."}
      </p>

      {/* Enrolled — disable form */}
      {enrolled && stage !== "enabled" && (
        <form onSubmit={disable} data-testid="tfa-disable-form" className="space-y-3">
          <label className="kicker block">Enter a current 6-digit code to disable</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            data-testid="tfa-disable-code"
            placeholder="123456"
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] focus:border-[var(--pa-gold)] focus:outline-none p-2.5 font-mono tracking-widest text-center"
          />
          <button
            type="submit"
            disabled={disabling || !disableCode.trim()}
            data-testid="tfa-disable-submit"
            className="text-xs text-red-400 hover:text-red-300 underline disabled:opacity-50"
          >
            {disabling ? "Disabling…" : "Disable 2FA"}
          </button>
        </form>
      )}

      {/* Idle — show enroll button */}
      {!enrolled && stage === "idle" && (
        <button
          onClick={startSetup}
          data-testid="tfa-start-setup"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm transition ${
            needsNow
              ? "bg-red-500 text-white hover:bg-red-400"
              : "bg-[var(--pa-gold)] text-[var(--pa-bg)] hover:bg-[var(--pa-gold-hover)]"
          }`}
        >
          <KeyRound size={14} />
          {needsNow ? "Enroll now (required)" : "Set up two-factor"}
        </button>
      )}

      {/* Setup stage — QR + verify */}
      {!enrolled && stage === "setup" && setupPayload && (
        <div className="space-y-4">
          <ol className="text-xs text-[var(--pa-text-muted)] space-y-1.5 list-decimal list-inside">
            <li>Open your authenticator app (Google Authenticator, 1Password, Authy, etc.)</li>
            <li>Scan the QR code below, or paste the secret manually</li>
            <li>Enter the 6-digit code your app generates to finish enrollment</li>
          </ol>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <img
              src={setupPayload.qr_data_uri}
              alt="2FA QR code"
              data-testid="tfa-qr"
              className="w-40 h-40 bg-white p-2 rounded-md border border-[var(--pa-border)]"
            />
            <div className="flex-1 space-y-2 min-w-0">
              <div className="kicker">Manual secret (if you cannot scan)</div>
              <code
                className="block w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-2 text-xs font-mono text-[var(--pa-gold)] break-all"
                data-testid="tfa-secret"
              >
                {setupPayload.secret}
              </code>
              <div className="kicker pt-2">Account label</div>
              <div className="text-xs text-[var(--pa-text-muted)] font-mono">
                {setupPayload.issuer} : {setupPayload.label}
              </div>
            </div>
          </div>
          <form onSubmit={confirmSetup} className="space-y-3 max-w-xs">
            <label className="kicker block">6-digit code from your app</label>
            <input
              type="text"
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="tfa-confirm-code"
              placeholder="123456"
              className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] focus:border-[var(--pa-gold)] focus:outline-none p-2.5 font-mono tracking-widest text-center text-lg"
            />
            <button
              type="submit"
              disabled={verifying || !code.trim()}
              data-testid="tfa-confirm-submit"
              className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-4 py-2 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 flex items-center gap-2"
            >
              {verifying ? (<><Loader2 size={14} className="animate-spin" /> Verifying…</>) : "Verify & enable"}
            </button>
            <button
              type="button"
              onClick={() => { setStage("idle"); setSetupPayload(null); setCode(""); }}
              className="block text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] underline"
              data-testid="tfa-cancel-setup"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Just-enabled — show recovery codes once */}
      {stage === "enabled" && recoveryCodes && (
        <div className="space-y-4">
          <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-300">
            <strong className="block mb-1">Save these recovery codes</strong>
            You will only see them once. Each code works one time if you lose access to your authenticator app.
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="tfa-recovery-list">
            {recoveryCodes.map((c) => (
              <code key={c} className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-2 text-xs font-mono text-[var(--pa-text)] text-center">
                {c}
              </code>
            ))}
          </div>
          <button
            onClick={copyRecovery}
            data-testid="tfa-copy-recovery"
            className="inline-flex items-center gap-2 text-xs text-[var(--pa-gold)] hover:underline"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy all codes"}
          </button>
          <button
            onClick={() => { setStage("idle"); setRecoveryCodes(null); }}
            data-testid="tfa-done"
            className="block text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] underline"
          >
            I've saved them — done
          </button>
        </div>
      )}

      {/* Footer policy */}
      <div className="mt-4 pt-3 border-t border-[var(--pa-border)] text-[10px] text-[var(--pa-text-dim)]">
        Policy: {status?.policy?.soft_enroll_grace_days ?? 7}-day soft enrollment ·{" "}
        idle timeout {status?.policy?.idle_timeout_minutes ?? 30} min ·{" "}
        account locks after {status?.policy?.max_failed_logins ?? 5} failed attempts
      </div>
    </div>
  );
}
